import { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Loader2, Upload, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '@/components/ProtectedRoute';
import { API, API_BASE } from '@/lib/api';

/**
 * Resolve a storage URL to an absolute URL.
 * Works for: backend-relative ("/api/uploads/..."), absolute http URLs.
 */
export function resolveImageUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  if (url.startsWith('/api/')) return `${API_BASE}${url}`;
  return url;
}

/**
 * Simple image upload component.
 * - value: current URL
 * - onChange: called with new URL after upload, or with '' when cleared
 * - label: optional label
 * - aspect: Tailwind aspect class for preview (default aspect-video)
 */
export const ImageUploader = ({ value, onChange, label, aspect = 'aspect-video', testId = 'image-uploader' }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Merci de choisir une image (JPEG, PNG, WEBP)');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image trop grande (8 Mo max)');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/uploads/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
      toast.success('Image téléchargée');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const resolved = resolveImageUrl(value);

  return (
    <div className="w-full" data-testid={testId}>
      {label && <label className="block text-[10px] tracking-widest uppercase text-muted-foreground mb-1.5">{label}</label>}

      {resolved ? (
        <div className={`relative group rounded-xl overflow-hidden border border-border ${aspect} bg-muted`}>
          <img loading="lazy" decoding="async" src={resolved} alt="Aperçu" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-dark/0 group-hover:bg-dark/50 transition-all duration-300 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
            <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} className="bg-white" data-testid={`${testId}-replace`}>
              <Upload className="h-3.5 w-3.5 mr-1.5" /> Remplacer
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => onChange('')} className="bg-white text-destructive" data-testid={`${testId}-clear`}>
              <X className="h-3.5 w-3.5 mr-1.5" /> Retirer
            </Button>
          </div>
        </div>
      ) : (
        <div
          className={`relative rounded-xl border-2 border-dashed border-border hover:border-primary/40 transition-colors ${aspect} bg-muted/50 flex flex-col items-center justify-center cursor-pointer text-center p-6`}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid={`${testId}-dropzone`}
        >
          {uploading ? (
            <>
              <Loader2 className="h-6 w-6 text-primary animate-spin mb-2" />
              <p className="text-xs text-muted-foreground">Envoi en cours...</p>
            </>
          ) : (
            <>
              <ImageIcon className="h-8 w-8 text-muted-foreground/40 mb-2" />
              <p className="text-sm text-dark font-medium">Clique ou glisse une image</p>
              <p className="text-[10px] text-muted-foreground mt-1">JPEG, PNG, WEBP · 8 Mo max</p>
            </>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testId}-input`}
      />
    </div>
  );
};
